import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { BackendResponse } from '../types';
import { useI18n } from '../hooks/useI18n';
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Leaf,
  ArrowLeft,
  Shield,
  Check
} from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const { t, isRTL } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [token, setToken] = useState<string>('');
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError(t('resetPassword.invalidResetLink'));
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('resetPassword.passwordsDoNotMatch'));
      return;
    }

    if (formData.newPassword.length < 8) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response: BackendResponse = await authAPI.resetPassword(token, formData.newPassword);
      setMessage(response.message || t('resetPassword.successMessage'));

      // Redirect to login with success message
      navigate('/login?resetSuccess=true');
    } catch (error: unknown) {
      const apiError = error as any;
      setError(apiError.response?.data?.message || t('resetPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { label: t('auth.resetPassword.requirements.length'), met: formData.newPassword.length >= 8 },
    { label: t('auth.resetPassword.requirements.uppercase'), met: /[A-Z]/.test(formData.newPassword) },
    { label: t('auth.resetPassword.requirements.lowercase'), met: /[a-z]/.test(formData.newPassword) },
    { label: t('auth.resetPassword.requirements.number'), met: /\d/.test(formData.newPassword) },
    { label: t('auth.resetPassword.requirements.special'), met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) }
  ];

  if (!token) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex items-center justify-center px-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="w-full max-w-md">
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-secondary-900">{t('auth.resetPassword.invalidResetLink')}</h2>
              <p className="text-secondary-600">{t('auth.resetPassword.invalidResetLinkDescription')}</p>
            </div>
            <div className="card-body">
              {error && (
                <div className={`mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                  <p className="text-error-800">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <Link
                  to="/forgot-password"
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  <Shield className="w-4 h-4" />
                  <span>{t('auth.resetPassword.requestNewLink')}</span>
                </Link>

                <Link
                  to="/login"
                  className="btn-outline w-full flex items-center justify-center space-x-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>{t('auth.resetPassword.backToLogin')}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <div className="flex items-center justify-center mb-8">
              <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                  <img src="/solvingers.svg" />
                </div>
                <h1 className="text-4xl font-bold">Facturelik</h1>
              </Link>
            </div>

            <h2 className="text-3xl font-semibold mb-4">{t('auth.resetPassword.setNewPassword')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('auth.resetPassword.setNewPasswordDescription')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className='text-lg font-semibold mb-2'>{t('auth.resetPassword.securePassword')}</h3>
                  <p className='text-white/80'>{t('auth.resetPassword.securePasswordDescription')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.resetPassword.oneTimeLink')}</h3>
                  <p className="text-white/80">{t('auth.resetPassword.oneTimeLinkDescription')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3">
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('auth.resetPassword.title')}</h2>
            <p className="text-secondary-600">{t('auth.resetPassword.enterNewPasswordBelow')}</p>
          </div>

          {message && (
            <div className={`mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{message}</p>
            </div>
          )}

          {error && (
            <div className={`mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-error-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className="form-label">
                {t('auth.resetPassword.newPassword')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder={t('auth.resetPassword.enterNewPasswordPlaceholder')}
                  className="form-input pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  )}
                </button>
              </div>

              {/* Password Requirements */}
              {formData.newPassword && (
                <div className="mt-3 p-3 bg-secondary-50 rounded-lg">
                  <p className='text-sm font-medium text-secondary-700 mb-2'>{t('auth.resetPassword.passwordRequirements')}:</p>
                  <div className="space-y-1">
                    {passwordRequirements.map((req, index) => (
                      <div key={index} className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'}`}>
                        {req.met ? (
                          <Check className="w-4 h-4 text-success-600" />
                        ) : (
                          <div className="w-4 h-4 border-2 border-secondary-300 rounded-full"></div>
                        )}
                        <span className={`text-xs ${req.met ? 'text-success-700' : 'text-secondary-600'}`}>
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="form-label">
                {t('auth.resetPassword.confirmPassword')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  className={`form-input pl-10 pr-10 ${formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                    ? 'border-error-500 focus:ring-error-500'
                    : ''
                    }`}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  )}
                </button>
              </div>
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                <p className="mt-1 text-sm text-error-600">{t('auth.resetPassword.passwordsDoNotMatch')}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 spinner"></div>
                  <span>{t('auth.resetPassword.resetting')}</span>
                </div>
              ) : (
                t('auth.resetPassword.resetButton')
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('auth.resetPassword.backToLogin')}</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
